import { Controller } from "@hotwired/stimulus"

// Every half hour from 6 am to 11:30 pm
const OPENS = Array.from({ length: 35 }, (_, index) => 6 * 60 + index * 30)
const CLOSES = Array.from({ length: 35 }, (_, index) => 6 * 60 + 30 + index * 30)
const clock = (minutes) => `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`
const spoken = (minutes) => {
  const hour = Math.floor(minutes / 60) % 24
  const suffix = hour < 12 ? "AM" : "PM"
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${suffix}`
}
const minutesOf = (value) => {
  if (!value) return null
  const [hour, minute] = value.split(":").map(Number)
  return Number.isFinite(hour) ? hour * 60 + (minute || 0) : null
}

// Opens and closes in two scrolling columns. Writes the form's start and end times, or marks the times
// as not set yet.
export default class extends Controller {
  static targets = ["trigger", "label", "popover", "opensColumn", "closesColumn", "opensLabel", "closesLabel", "opens", "closes", "allDay", "allDayField"]

  connect() {
    this.opensAt = minutesOf(this.opensTarget.value)
    this.closesAt = minutesOf(this.closesTarget.value)
    this.allDayTarget.checked = this.allDayFieldTarget.value === "true"
    this.paint()
  }

  open() {
    this.popoverTarget.hidden = false
    this.renderColumns()
    this.popoverTarget.scrollIntoView({ block: "nearest" })
    this.element.querySelectorAll(".v2-hour.is-selected").forEach((selected) => selected.scrollIntoView({ block: "center" }))
  }

  close() {
    this.popoverTarget.hidden = true
  }

  closeOutside(event) {
    // A day or hour that was just picked has been redrawn, so it's no longer in the page
    if (!this.popoverTarget.hidden && event.target.isConnected && !this.element.contains(event.target)) this.close()
  }

  done() {
    this.close()
  }

  choose(event) {
    const minutes = Number(event.currentTarget.dataset.minutes)
    if (event.currentTarget.dataset.column === "opens") {
      this.opensAt = minutes
      if (this.closesAt === null || this.closesAt <= minutes) this.closesAt = Math.min(minutes + 180, CLOSES[CLOSES.length - 1])
    } else {
      this.closesAt = minutes
    }
    this.allDayTarget.checked = false
    this.write()
    this.renderColumns()
  }

  toggleAllDay() {
    this.write()
  }

  write() {
    const allDay = this.allDayTarget.checked
    this.allDayFieldTarget.value = allDay ? "true" : "false"
    this.opensTarget.value = !allDay && this.opensAt !== null ? clock(this.opensAt) : ""
    this.closesTarget.value = !allDay && this.closesAt !== null ? clock(this.closesAt) : ""
    this.paint()
    this.opensTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  paint() {
    const allDay = this.allDayTarget.checked
    const set = allDay || this.opensAt !== null
    const hours = this.closesAt === null ? `From ${spoken(this.opensAt)}` : `${spoken(this.opensAt)} – ${spoken(this.closesAt)}`
    this.labelTarget.textContent = allDay ? "Times not set yet" : set ? hours : "Opens – closes"
    this.triggerTarget.classList.toggle("is-set", set)
    this.opensLabelTarget.textContent = this.opensAt !== null && !allDay ? spoken(this.opensAt) : "–"
    this.closesLabelTarget.textContent = this.closesAt !== null && !allDay ? spoken(this.closesAt) : "–"
  }

  renderColumns() {
    const column = (name, times, selected, after) => times
      .filter((minutes) => after === null || minutes > after)
      .map((minutes) => `<button type="button" role="option" class="v2-hour${minutes === selected ? " is-selected" : ""}"
        aria-selected="${minutes === selected}" data-column="${name}" data-minutes="${minutes}" data-action="hours-picker#choose">${spoken(minutes)}</button>`)
      .join("")
    this.opensColumnTarget.innerHTML = column("opens", OPENS, this.opensAt, null)
    this.closesColumnTarget.innerHTML = column("closes", CLOSES, this.closesAt, this.opensAt)
  }
}
